// iconMap — Lucide icons per game domain, replacing emoji chrome in
// the UI. Config `emoji` fields stay (tests + narrative fallback);
// components render these instead.

import {
  // Buildings
  ConciergeBell, ShoppingBag, DoorOpen, Coffee, CarFront, Waves,
  GraduationCap, Armchair, Newspaper,
  // Staff
  Headphones, Dumbbell, Wrench, CupSoda, Stethoscope, HeartPulse, Brain, Apple,
  // Services
  LandPlot, Users, Baby, PartyPopper, Store,
  // Club levels
  Mountain, CircleDot, Construction, Building, Star, Sparkles, Building2, Crown, Landmark, Globe2,
  // Weather
  Sun, Cloud, CloudRain, CloudLightning,
  // Tournaments
  Medal, Award, Trophy,
  type LucideIcon,
} from 'lucide-react'
import type { TournamentType } from '@/config/tournamentConfig'
import type { WeatherType } from '@/config/eventsConfig'

// Open maps (facility/staff ids are open sets); GameIcon falls back to a
// generic icon for ids without an entry.
export const BUILDING_ICONS: Record<string, LucideIcon> = {
  reception: ConciergeBell,
  shop: ShoppingBag,
  locker: DoorOpen,
  cafe: Coffee,
  parking: CarFront,
  pool: Waves,
  gym: Dumbbell,
  academy: GraduationCap,
  kids_zone: Baby,
  vip_box: Armchair,
  press_room: Newspaper,
  solar: Sun,
}

export const STAFF_ICONS: Record<string, LucideIcon> = {
  receptionist: Headphones,
  coach: Dumbbell,
  maintenance: Wrench,
  barista: CupSoda,
  doctor: Stethoscope,
  physio: HeartPulse,
  psychologist: Brain,
  nutritionist: Apple,
}

export const SERVICE_ICONS: Record<string, LucideIcon> = {
  court_rental: LandPlot,
  group_classes: Users,
  kids_camp: Baby,
  private_events: PartyPopper,
  pro_shop: Store,
  cafeteria: Coffee,
}

export const CLUB_LEVEL_ICONS: Record<number, LucideIcon> = {
  0: Mountain,
  1: CircleDot,
  2: Construction,
  3: Building,
  4: Star,
  5: Sparkles,
  6: Building2,
  7: Crown,
  8: Landmark,
  9: Globe2,
}

export const WEATHER_ICONS: Record<WeatherType, LucideIcon> = {
  sunny: Sun,
  cloudy: Cloud,
  rainy: CloudRain,
  stormy: CloudLightning,
}

export const TOURNAMENT_ICONS: Record<TournamentType, LucideIcon> = {
  amateur: CircleDot,
  intermediate: Medal,
  professional: Award,
  championship: Trophy,
}

export type IconDomain = 'building' | 'staff' | 'service' | 'clubLevel' | 'weather' | 'tournament'

interface GameIconProps {
  domain: IconDomain
  id: string | number
  size?: number
  className?: string
  style?: React.CSSProperties
}

const DOMAIN_MAPS: Record<IconDomain, Record<string | number, LucideIcon>> = {
  building: BUILDING_ICONS,
  staff: STAFF_ICONS,
  service: SERVICE_ICONS,
  clubLevel: CLUB_LEVEL_ICONS,
  weather: WEATHER_ICONS,
  tournament: TOURNAMENT_ICONS,
}

export function GameIcon({ domain, id, size = 16, className, style }: GameIconProps) {
  const Icon = DOMAIN_MAPS[domain]?.[id] ?? CircleDot
  return <Icon size={size} className={className} style={style} />
}
